import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { theme } from '../../../../theme/theme'
import { Icon, IconChoices } from '../../../common/Icon'
import { PostAvatar } from '../PostAvatar'
import { PostImportant } from '../PostImportant'
import { PostPinned } from '../PostPinned'
import { PostAuthor } from '../PostAuthor'
import { formatDate } from '../../../../helpers/date'
import { downloadCsv } from '../../../../helpers/csv'
import { TextTooltip } from '../../../common/Tooltip'
import { Button, ButtonVariant } from '../../../common/Button'
import { Dropdown, Option } from '../../../common/Dropdown'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { removeMarkdown, replaceHtml } from '../../../../helpers/string'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { CreateEditPostModal } from '../../CreateEditPostModal'
import { ReportPostModal } from '../../ReportPostModal'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { useModal } from '../../../../hooks/useModal'
import { societyQueries } from '../../../../api/societies'
import { remove } from '../../../../api/posts'
import { getVotes } from '../../../../api/polls'
import { NPolls } from '../../../../interfaces/services/polls.interfaces'
import { i18nSocietyType } from '../../../../i18n'
import { useAuth } from '../../../../hooks/useAuth'
import { PostRow } from '../../../../generated/api/post-row'
import { invalidatePostRows, removePostRow } from '../../postRowCache'
import { useTogglePin } from '../../useTogglePin'
import { useCanPrintPost } from '../../PostPrint/useCanPrintPost'

interface PostHeaderProps {
  row: PostRow
  disabled?: boolean
}

export const PostHeader = ({
  row,
  disabled,
}: PostHeaderProps): React.ReactNode | null => {
  const { translate } = useAppTranslation()
  const navigate = useNavigate()
  const { userId } = useAuth()
  const {
    id: postId,
    role,
    createdAt,
    message,
    isAlert,
    isPinned,
    audience,
    author,
    society,
    actions,
  } = row
  const { data: societies } = useQuery(societyQueries.getUserSocieties(userId))
  const { setToastNotification } = useToastNotifications()
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)
  const {
    show: showEditPostModal,
    open: openEditPostModal,
    close: closeEditPostModal,
  } = useModal({ preventClose: true })

  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()

  const {
    show: showReportPostModal,
    open: openReportPostModal,
    close: closeReportPostModal,
  } = useModal()

  const societyType = i18nSocietyType(society.type)
  const exportablePoll = row.polls.find((poll) => poll.canExport)
  const societyLockedForUser = !actions.canLike && !row.isScheduled

  const userAvatarOnClick = (): void => {
    if (!disabled && author) {
      navigate(
        reverseUrl('user:society-detail', {
          id: author.id,
          societyId: society.id,
        })
      )
    }
  }

  const exportVotes = async (): Promise<void> => {
    if (!exportablePoll) {
      return
    }
    try {
      const resp = await getVotes(exportablePoll.id)
      const votes = (resp.data as NPolls.NGetVotes.IResponse)?.data || []
      downloadCsv(votes, `${exportablePoll.title}.csv`)
    } catch (error) {
      captureException(error)
      setToastNotification(
        ToastType.DANGER,
        translate('poll.flashMessage.exportVotesFailure')
      )
    }
  }

  const hasRecipients = audience.scope !== 'everyone'

  const getRecipientsTitle = (): string | undefined => {
    const base = translate('post.tooltip.visibility.base')
    switch (audience.scope) {
      case 'admins':
        return `${base} ${translate('post.tooltip.visibility.admins', {
          context: societyType,
        })}`
      case 'board':
        return `${base} ${translate('post.tooltip.visibility.board', {
          context: societyType,
        })}`
      case 'groups':
        return `${base} ${translate('post.tooltip.visibility.groups', {
          context: societyType,
        })}:`
      default:
        return undefined
    }
  }

  const getRecipientsText = (): string | undefined => {
    return audience.groupNames.length > 0
      ? audience.groupNames.join('\n')
      : undefined
  }

  const queryClient = useQueryClient()

  const mutateTogglePin = useTogglePin(row)
  const { canPrint } = useCanPrintPost(society.id)

  const { mutate: mutateRemovePost } = useMutation({
    mutationFn: async () => {
      return remove(postId)
    },
    meta: { skipGlobalInvalidate: true },
    onSuccess: () => {
      removePostRow(queryClient, postId)
      setToastNotification(
        ToastType.SUCCESS,
        translate('post.flashMessage.deletePostSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('post.flashMessage.deletePostFailure')
      )
    },
    onSettled: () => invalidatePostRows(queryClient),
  })

  const onCopyText = (): void => {
    navigator.clipboard.writeText(removeMarkdown(replaceHtml(message)))
    setToastNotification(
      ToastType.SUCCESS,
      translate('common.copiedToClipboard')
    )
  }

  const onPrint = (): void => {
    navigate(reverseUrl('post:print', { id: postId }))
  }

  const getDropdownOptions = (): Option[] => {
    const options: Option[] = [
      ...(actions.canPin && !disabled
        ? [
            {
              value: 'pin',
              label: translate(
                isPinned ? 'common.actions.unpin' : 'common.actions.pin'
              ),
              onClick: () => mutateTogglePin(!isPinned),
              icon: IconChoices.PIN,
            },
          ]
        : []),
      {
        value: 'copy',
        label: translate('common.actions.copyText'),
        onClick: onCopyText,
        icon: IconChoices.COPY,
      },
      ...(!disabled && canPrint
        ? [
            {
              value: 'print',
              label: translate('common.actions.print'),
              onClick: onPrint,
              icon: IconChoices.PRINT,
            },
          ]
        : []),
      ...(!disabled && actions.canEdit
        ? [
            {
              value: 'edit',
              label: translate('common.actions.edit'),
              onClick: openEditPostModal,
              icon: IconChoices.EDIT,
              disabled: societyLockedForUser,
            },
          ]
        : []),
      ...(!disabled && actions.canReport
        ? [
            {
              value: 'report',
              label: translate('common.actions.report'),
              onClick: openReportPostModal,
              icon: IconChoices.REPORT,
            },
          ]
        : []),
      ...(!disabled && exportablePoll
        ? [
            {
              value: 'downloadPoll',
              label: translate('poll.result'),
              onClick: exportVotes,
              icon: IconChoices.DOWNLOAD,
            },
          ]
        : []),
      ...(!disabled && actions.canDelete
        ? [
            {
              value: 'delete',
              label: translate('common.actions.delete'),
              onClick: openConfirmationModal,
              icon: IconChoices.DELETE_TRASH,
              destructive: true,
            },
          ]
        : []),
    ]
    return options
  }

  const deletePost = async (): Promise<void> => {
    mutateRemovePost()
  }

  const dropdownOptions = getDropdownOptions()

  const authorBlock = (
    <div className="flex min-w-0 grow gap-2">
      <PostAvatar
        role={role}
        author={author}
        society={society}
        userHasMultipleSocieties={societies && societies.length > 1}
        onClick={userAvatarOnClick}
      />
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex">
          <PostAuthor
            role={role}
            fullName={author?.fullName}
            societyType={society.type}
          />
        </div>
        <p className="text-xs">{formatDate(createdAt)}</p>
      </div>
    </div>
  )

  const moreButton = dropdownOptions.length > 0 && (
    <div className={`flex gap-3 ${isPinned ? 'self-start' : 'items-center'}`}>
      {hasRecipients && (
        <TextTooltip
          title={getRecipientsTitle()}
          text={getRecipientsText()}
          referenceElement={referenceElement}
        >
          <Icon icon={IconChoices.GROUPS} ref={setReferenceElement} />
        </TextTooltip>
      )}
      <Dropdown
        options={dropdownOptions}
        dropdownContentWidth="w-64"
        disabled={disabled}
      >
        <Button
          variant={ButtonVariant.TEXT}
          className={`h-10 w-10 border-none bg-transparent hover:bg-neutral-90 ${isPinned ? 'pt-0' : ''}`}
          icon={IconChoices.MORE}
          disableTabIndex
        />
      </Dropdown>
    </div>
  )

  const showSocietyName = !!society.name && !!societies && societies.length > 1

  const societyNameLabel = showSocietyName && (
    <p
      className="uppercase text-brandGreen"
      style={theme.textVariants.fineprintBold}
    >
      {society.name}
    </p>
  )

  let headerBody
  if (isPinned || showSocietyName) {
    headerBody = (
      <>
        <div className="flex items-center justify-between gap-2">
          {isPinned ? (
            <PostPinned> {societyNameLabel} </PostPinned>
          ) : (
            societyNameLabel
          )}
          {moreButton}
        </div>
        {authorBlock}
      </>
    )
  } else {
    headerBody = (
      <div className="flex justify-between">
        {authorBlock}
        {moreButton}
      </div>
    )
  }

  return (
    <>
      {isAlert && <PostImportant />}
      {headerBody}
      {showEditPostModal && (
        <CreateEditPostModal
          show={showEditPostModal}
          close={closeEditPostModal}
          postId={postId}
          attachments={row}
        />
      )}
      <ReportPostModal
        show={showReportPostModal}
        close={closeReportPostModal}
        postId={postId}
      />
      <ConfirmationModal
        title={translate('post.alert.postDeleteTitle')}
        description={translate('post.alert.postDeleteText', {
          context: societyType,
        })}
        show={showConfirmationModal}
        close={closeConfirmationModal}
        onConfirm={deletePost}
        deleteMode
      />
    </>
  )
}
